 import React, { useState, useEffect, useCallback } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'https://api.esgova.io';
const NAVY = '#0a1628', NAVY2 = '#112240', NAVY3 = '#1a3a6b';
const GOLD = '#c9a84c', GOLD2 = '#e8c96d', LIGHT = '#f0f4fa';

const tok = () => localStorage.getItem('esgova_token');
const hdrs = () => ({ 'Content-Type': 'application/json', ...(tok() ? { Authorization: `Bearer ${tok()}` } : {}) });
const api = async (path, opts = {}) => {
  const r = await fetch(`${BASE}${path}`, { headers: hdrs(), ...opts });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Error'); }
  return r.json();
};
const post = (path, body) => api(path, { method: 'POST', body: JSON.stringify(body) });
const patch = (path, body) => api(path, { method: 'PATCH', body: JSON.stringify(body) });

const Card = ({ children, style = {} }) => (
  <div style={{ background: '#fff', borderRadius: 14, padding: '1.4rem', border: '1px solid #e8ecf4', boxShadow: '0 2px 12px rgba(10,22,40,.05)', ...style }}>{children}</div>
);
const Btn = ({ children, onClick, style = {}, disabled = false }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ background: `linear-gradient(135deg,${NAVY2},${NAVY3})`, color: GOLD, border: 'none', padding: '.7rem 1.4rem', borderRadius: 10, fontWeight: 800, fontSize: '.88rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .6 : 1, ...style }}>
    {children}
  </button>
);
const Badge = ({ label, color, bg }) => (
  <span style={{ background: bg || color + '18', color, fontSize: '.72rem', padding: '.2rem .65rem', borderRadius: 100, fontWeight: 700 }}>{label}</span>
);
const statusColor = s => ({ Complete: '#22c55e', 'In Progress': GOLD, 'Under Review': '#3b82f6', Draft: '#9ca3af' }[s] || '#9ca3af');
const Spinner = ({ text = 'Loading...' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '3rem', justifyContent: 'center' }}>
    <div style={{ width: 24, height: 24, border: `3px solid ${GOLD}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    <span style={{ fontWeight: 600, color: '#6b7280' }}>{text}</span>
  </div>
);
const ErrBox = ({ msg, onRetry }) => (
  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '1rem', color: '#dc2626', fontSize: '.88rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
    ⚠️ {msg}
    {onRetry && <button onClick={onRetry} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 700, fontSize: '.8rem', textDecoration: 'underline' }}>Retry</button>}
  </div>
);
const Header = ({ title, sub, action }) => (
  <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: `2px solid ${LIGHT}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
    <div>
      <h1 style={{ fontSize: '1.55rem', fontWeight: 900, color: NAVY, letterSpacing: '-.3px' }}>{title}</h1>
      {sub && <p style={{ color: '#6b7280', fontSize: '.85rem', marginTop: '.3rem' }}>{sub}</p>}
    </div>
    {action}
  </div>
);
const Field = ({ label, value, onChange, placeholder, type = 'text', onEnter }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ fontSize: '.72rem', fontWeight: 800, color: NAVY, display: 'block', marginBottom: '.4rem', letterSpacing: .5 }}>{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onEnter && onEnter()}
      placeholder={placeholder}
      style={{ width: '100%', padding: '.8rem 1rem', borderRadius: 10, border: '2px solid #e5e7eb', fontSize: '.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
  </div>
);

// ── LOGIN ──────────────────────────────────────
const Login = ({ onLogin }) => {
  const [form, setForm] = useState({ email: '', password: '', full_name: '', organization: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('login');

  const submit = async () => {
    setLoading(true); setErr('');
    try {
      const path = tab === 'login' ? '/auth/login' : '/auth/register';
      const body = tab === 'login' ? { email: form.email, password: form.password } : { email: form.email, password: form.password, full_name: form.full_name, organization: form.organization, role: 'ESG Analyst' };
      const data = await post(path, body);
      localStorage.setItem('esgova_token', data.access_token);
      onLogin(data.user);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(145deg,${NAVY},${NAVY3})`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}`}</style>
      <div style={{ background: '#fff', borderRadius: 24, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 30px 80px rgba(0,0,0,.4)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.8rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '.6rem', background: NAVY, borderRadius: 12, padding: '.6rem 1.2rem', marginBottom: '.8rem' }}>
            <div style={{ width: 28, height: 28, background: `linear-gradient(135deg,${GOLD},${GOLD2})`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: NAVY, fontSize: '.8rem' }}>E</div>
            <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.1rem', letterSpacing: 2 }}>ESGOVA</span>
          </div>
          <div style={{ fontSize: '.8rem', color: '#6b7280', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700, color: NAVY }}>US Institute of Diplomacy and Human Rights</span><br />Washington, DC
          </div>
        </div>
        <div style={{ display: 'flex', background: LIGHT, borderRadius: 10, padding: '.3rem', marginBottom: '1.5rem' }}>
          {['login', 'register'].map(t => (
            <button key={t} onClick={() => { setTab(t); setErr(''); }}
              style={{ flex: 1, padding: '.55rem', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '.85rem', background: tab === t ? NAVY : 'transparent', color: tab === t ? GOLD : '#6b7280' }}>
              {t === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>
        {tab === 'register' && <>
          <Field label="FULL NAME" value={form.full_name} onChange={v => setForm(p => ({ ...p, full_name: v }))} placeholder="Dr. James Whitfield" />
          <Field label="ORGANIZATION" value={form.organization} onChange={v => setForm(p => ({ ...p, organization: v }))} placeholder="USIDHR" />
        </>}
        <Field label="EMAIL" value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} placeholder="admin@esgova.usidhr.org" type="email" />
        <Field label="PASSWORD" value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))} placeholder="••••••••" type="password" onEnter={submit} />
        {err && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '.7rem 1rem', borderRadius: 8, fontSize: '.82rem', marginBottom: '1rem' }}>⚠️ {err}</div>}
        <Btn onClick={submit} disabled={loading} style={{ width: '100%', padding: '.9rem', justifyContent: 'center', display: 'flex' }}>
          {loading ? 'Please wait...' : tab === 'login' ? 'Sign In to ESGOVA →' : 'Create Account →'}
        </Btn>
        <div style={{ marginTop: '1.2rem', background: LIGHT, borderRadius: 10, padding: '.9rem', fontSize: '.75rem', color: '#555' }}>
          <div style={{ fontWeight: 800, color: NAVY, marginBottom: '.4rem' }}>🔐 Demo credentials</div>
          <div>admin@esgova.usidhr.org / Admin@ESGOVA2025</div>
        </div>
      </div>
    </div>
  );
};

// ── DASHBOARD ──────────────────────────────────
const Dashboard = ({ user, onNav }) => {
  const [reports, setReports] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [r, t] = await Promise.all([api('/reports'), api('/tasks')]);
      setReports(r.reports || []); setTasks(t.tasks || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner text="Loading dashboard..." />;

  const kpis = [
    { label: 'Active Reports', val: reports.filter(r => r.status !== 'Complete').length, icon: '📋', color: NAVY3 },
    { label: 'Open Tasks', val: tasks.filter(t => !t.is_done).length, icon: '⚡', color: '#f59e0b' },
    { label: 'Avg. Progress', val: reports.length ? Math.round(reports.reduce((a, r) => a + r.progress, 0) / reports.length) + '%' : '0%', icon: '🎯', color: '#22c55e' },
    { label: 'Total Reports', val: reports.length, icon: '📊', color: '#8b5cf6' },
  ];

  return (
    <div>
      <Header title={`Welcome, ${user?.full_name?.split(' ').slice(-1)[0] || 'User'} 👋`}
        sub={`${user?.organization?.name || 'USIDHR'} · ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`} />
      {err && <ErrBox msg={err} onRetry={load} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {kpis.map(k => (
          <Card key={k.label} style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${NAVY},${GOLD})` }} />
            <div style={{ fontSize: '1.6rem', marginBottom: '.4rem' }}>{k.icon}</div>
            <div style={{ fontSize: '2rem', fontWeight: 900, color: k.color }}>{k.val}</div>
            <div style={{ fontSize: '.78rem', color: '#6b7280', marginTop: '.2rem', fontWeight: 600 }}>{k.label}</div>
          </Card>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '1.5rem' }}>
        <Card>
          <div style={{ fontWeight: 800, fontSize: '.95rem', color: NAVY, marginBottom: '1.2rem' }}>📊 Report Progress</div>
          {reports.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
              No reports yet — <button onClick={() => onNav('reports')} style={{ background: 'none', border: 'none', color: NAVY3, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>create your first</button>
            </div>
          ) : reports.map(r => (
            <div key={r.id} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.35rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '.85rem', color: NAVY }}>{r.name}</span>
                  <Badge label={r.framework} color={NAVY} bg={LIGHT} />
                </div>
                <Badge label={r.status} color={statusColor(r.status)} />
              </div>
              <div style={{ height: 7, background: '#eef0f5', borderRadius: 4 }}>
                <div style={{ height: '100%', width: `${r.progress}%`, background: `linear-gradient(90deg,${NAVY3},${GOLD})`, borderRadius: 4 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', color: '#9ca3af', marginTop: '.3rem' }}>
                <span>{r.progress}% complete</span><span>Due {r.due_date || 'TBD'}</span>
              </div>
            </div>
          ))}
        </Card>
        <Card>
          <div style={{ fontWeight: 800, fontSize: '.95rem', color: NAVY, marginBottom: '1rem' }}>⚡ Open Tasks</div>
          {tasks.filter(t => !t.is_done).slice(0, 5).map(t => (
            <div key={t.id} style={{ display: 'flex', gap: '.7rem', alignItems: 'flex-start', padding: '.65rem 0', borderBottom: `1px solid ${LIGHT}` }}>
              <input type="checkbox" onChange={() => patch(`/tasks/${t.id}/complete`, {}).then(load)} style={{ marginTop: 3, accentColor: NAVY, cursor: 'pointer' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '.83rem', fontWeight: 700, color: NAVY }}>{t.title}</div>
                <div style={{ fontSize: '.72rem', color: '#9ca3af', marginTop: '.2rem' }}>Due {t.due_date}</div>
              </div>
              <Badge label={t.priority} color={t.priority === 'High' ? '#ef4444' : t.priority === 'Medium' ? '#f59e0b' : '#3b82f6'} />
            </div>
          ))}
          {tasks.filter(t => !t.is_done).length === 0 && <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9ca3af', fontSize: '.85rem' }}>🎉 All tasks complete!</div>}
          <Btn onClick={() => onNav('tasks')} style={{ marginTop: '.8rem', width: '100%', padding: '.6rem', fontSize: '.8rem', justifyContent: 'center', display: 'flex' }}>View All Tasks →</Btn>
        </Card>
      </div>
      <Card style={{ marginTop: '1.5rem', background: `linear-gradient(135deg,${NAVY},${NAVY3})`, border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: GOLD, fontWeight: 800, fontSize: '.9rem', marginBottom: '.3rem' }}>🏛️ USIDHR Compliance Reminder</div>
            <div style={{ color: 'rgba(255,255,255,.75)', fontSize: '.82rem' }}>CSRD first-wave deadline: April 30, 2025. Ensure all ESRS disclosures are finalized and auditor access is granted by March 31.</div>
          </div>
          <Btn onClick={() => onNav('reports')} style={{ background: `linear-gradient(135deg,${GOLD},${GOLD2})`, color: NAVY, flexShrink: 0, marginLeft: '1rem' }}>Open Reports →</Btn>
        </div>
      </Card>
    </div>
  );
};

// ── REPORTS ────────────────────────────────────
const Reports = ({ onNav }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', framework: 'CSRD', reporting_year: 2024 });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api('/reports'); setReports(d.reports || []); } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createReport = async () => {
    if (!form.name) return;
    setCreating(true);
    try { await post('/reports', form); setShowNew(false); setForm({ name: '', framework: 'CSRD', reporting_year: 2024 }); load(); }
    catch (e) { setErr(e.message); }
    setCreating(false);
  };

  if (loading) return <Spinner text="Loading reports..." />;

  return (
    <div>
      <Header title="📋 Reports" sub="All ESG & CSRD reports" action={<Btn onClick={() => setShowNew(true)}>+ New Report</Btn>} />
      {err && <ErrBox msg={err} onRetry={load} />}
      {showNew && (
        <Card style={{ marginBottom: '1.5rem', border: `2px solid ${GOLD}40` }}>
          <div style={{ fontWeight: 800, fontSize: '.95rem', color: NAVY, marginBottom: '1rem' }}>Create New Report</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 800, color: '#6b7280', display: 'block', marginBottom: '.3rem' }}>REPORT NAME</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. CSRD Annual Report 2024"
                style={{ width: '100%', padding: '.7rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '.88rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 800, color: '#6b7280', display: 'block', marginBottom: '.3rem' }}>FRAMEWORK</label>
              <select value={form.framework} onChange={e => setForm(p => ({ ...p, framework: e.target.value }))}
                style={{ width: '100%', padding: '.7rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '.88rem', background: '#fff' }}>
                {['CSRD', 'GRI', 'TCFD', 'VSME', 'HRDD', 'SFDR'].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 800, color: '#6b7280', display: 'block', marginBottom: '.3rem' }}>YEAR</label>
              <select value={form.reporting_year} onChange={e => setForm(p => ({ ...p, reporting_year: +e.target.value }))}
                style={{ width: '100%', padding: '.7rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '.88rem', background: '#fff' }}>
                {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '.7rem' }}>
            <Btn onClick={createReport} disabled={creating}>{creating ? 'Creating...' : 'Create Report'}</Btn>
            <button onClick={() => setShowNew(false)} style={{ padding: '.7rem 1.2rem', background: LIGHT, border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, color: '#6b7280' }}>Cancel</button>
          </div>
        </Card>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '1.2rem' }}>
        {reports.map(r => (
          <Card key={r.id} style={{ cursor: 'pointer', borderTop: `3px solid ${statusColor(r.status)}` }} onClick={() => onNav('builder', r)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.9rem' }}>
              <Badge label={r.framework} color="#fff" bg={NAVY} />
              <Badge label={r.status} color={statusColor(r.status)} />
            </div>
            <div style={{ fontWeight: 800, fontSize: '.95rem', color: NAVY, marginBottom: '.25rem' }}>{r.name}</div>
            <div style={{ fontSize: '.75rem', color: '#9ca3af', marginBottom: '1rem' }}>Updated {new Date(r.updated_at).toLocaleDateString()}</div>
            <div style={{ height: 8, background: '#eef0f5', borderRadius: 4, marginBottom: '.4rem' }}>
              <div style={{ height: '100%', width: `${r.progress}%`, background: `linear-gradient(90deg,${NAVY3},${GOLD})`, borderRadius: 4 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', color: '#6b7280' }}>
              <span>{r.progress}% complete</span><span>📅 {r.due_date || 'No deadline'}</span>
            </div>
          </Card>
        ))}
        {reports.length === 0 && !err && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <div style={{ fontWeight: 700, marginBottom: '.5rem' }}>No reports yet</div>
            <div style={{ fontSize: '.88rem' }}>Click "+ New Report" to create your first ESG report</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── TASKS ──────────────────────────────────────
const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api('/tasks'); setTasks(d.tasks || []); } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const complete = async (id) => {
    try { await patch(`/tasks/${id}/complete`, {}); setTasks(p => p.map(t => t.id === id ? { ...t, is_done: true } : t)); }
    catch (e) { setErr(e.message); }
  };

  if (loading) return <Spinner text="Loading tasks..." />;

  return (
    <div>
      <Header title="✅ Tasks" sub="Your reporting workload and deadlines" />
      {err && <ErrBox msg={err} onRetry={load} />}
      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: `2px solid ${LIGHT}` }}>
            {['', 'Task', 'Priority', 'Due Date', 'Status'].map(h => (
              <th key={h} style={{ padding: '.7rem .8rem', textAlign: 'left', fontSize: '.72rem', fontWeight: 800, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {tasks.map(t => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${LIGHT}`, opacity: t.is_done ? .45 : 1 }}>
                <td style={{ padding: '.8rem' }}><input type="checkbox" checked={t.is_done} onChange={() => !t.is_done && complete(t.id)} style={{ accentColor: NAVY, cursor: t.is_done ? 'default' : 'pointer' }} /></td>
                <td style={{ padding: '.8rem', fontSize: '.88rem', fontWeight: 700, color: NAVY, textDecoration: t.is_done ? 'line-through' : 'none' }}>{t.title}</td>
                <td style={{ padding: '.8rem' }}><Badge label={t.priority} color={t.priority === 'High' ? '#ef4444' : t.priority === 'Medium' ? '#f59e0b' : '#3b82f6'} /></td>
                <td style={{ padding: '.8rem', fontSize: '.82rem', color: '#6b7280', fontWeight: 600 }}>{t.due_date}</td>
                <td style={{ padding: '.8rem' }}><span style={{ fontSize: '.75rem', fontWeight: 700, color: t.is_done ? '#22c55e' : '#9ca3af' }}>{t.is_done ? '✅ Done' : '⏳ Pending'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {tasks.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>No tasks assigned yet</div>}
      </Card>
    </div>
  );
};

// ── AI ASSISTANT ───────────────────────────────
const AIAssistant = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [err, setErr] = useState('');
  const [custom, setCustom] = useState('');

  const PROMPTS = [
    { label: 'Draft ESRS E1 Climate Change', section_id: 'E1', section_label: 'Climate Change', framework: 'CSRD' },
    { label: 'Draft ESRS S1 Own Workforce', section_id: 'S1', section_label: 'Own Workforce', framework: 'CSRD' },
    { label: 'Draft ESRS G1 Business Conduct', section_id: 'G1', section_label: 'Business Conduct', framework: 'CSRD' },
    { label: 'Human Rights Due Diligence', section_id: 'HRDD', section_label: 'Human Rights Due Diligence', framework: 'HRDD' },
    { label: 'GRI 2 General Disclosures', section_id: 'GRI-2', section_label: 'General Disclosures', framework: 'GRI' },
    { label: 'TCFD Climate Risk Strategy', section_id: 'STRATEGY', section_label: 'Climate Strategy', framework: 'TCFD' },
  ];

  const draft = async (prompt) => {
    setLoading(true); setErr(''); setResult('');
    try {
      const d = await post('/ai/draft', { ...prompt, organization: user?.organization?.name || 'USIDHR', industry: 'institutional' });
      setResult(d.draft);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div>
      <Header title="🤖 AI Assistant" sub="Claude-powered ESRS disclosure drafting for USIDHR" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '1.5rem' }}>
        <Card>
          <div style={{ fontWeight: 800, fontSize: '.9rem', color: NAVY, marginBottom: '1rem' }}>⚡ Quick Prompts</div>
          {PROMPTS.map(p => (
            <button key={p.label} onClick={() => draft(p)} disabled={loading}
              style={{ width: '100%', padding: '.8rem 1rem', background: LIGHT, border: `1.5px solid #e8ecf4`, borderRadius: 10, textAlign: 'left', cursor: 'pointer', marginBottom: '.6rem', fontSize: '.83rem', color: NAVY, fontWeight: 700, lineHeight: 1.4, opacity: loading ? .6 : 1 }}>
              💬 {p.label}
            </button>
          ))}
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Card>
            <div style={{ fontWeight: 800, fontSize: '.9rem', color: NAVY, marginBottom: '.8rem' }}>Ask Anything</div>
            <div style={{ display: 'flex', gap: '.6rem' }}>
              <input value={custom} onChange={e => setCustom(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && custom && draft({ section_id: 'CUSTOM', section_label: custom, framework: 'CSRD' })}
                placeholder="Ask about CSRD, ESRS, GRI, human rights..."
                style={{ flex: 1, padding: '.7rem 1rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '.85rem', outline: 'none', fontFamily: 'inherit' }} />
              <Btn onClick={() => custom && draft({ section_id: 'CUSTOM', section_label: custom, framework: 'CSRD' })} disabled={!custom || loading}>Ask →</Btn>
            </div>
          </Card>
          <Card style={{ flex: 1, minHeight: 200 }}>
            <div style={{ fontWeight: 800, fontSize: '.9rem', color: NAVY, marginBottom: '1rem' }}>AI Response</div>
            {loading && <Spinner text="Generating ESRS-compliant draft..." />}
            {err && <ErrBox msg={err} />}
            {!loading && !result && !err && (
              <div style={{ color: '#9ca3af', fontSize: '.88rem', lineHeight: 1.8, textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '.8rem' }}>🤖</div>
                Select a prompt or type your question above
              </div>
            )}
            {result && (
              <div style={{ background: LIGHT, borderRadius: 10, padding: '1.2rem', fontSize: '.88rem', color: '#333', lineHeight: 1.8, borderLeft: `4px solid ${GOLD}` }}>
                <div style={{ fontWeight: 800, color: NAVY, fontSize: '.7rem', marginBottom: '.7rem', letterSpacing: 1 }}>🤖 AI DRAFT · ESRS COMPLIANT · USIDHR</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{result}</div>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '.6rem' }}>
                  <button onClick={() => navigator.clipboard.writeText(result)} style={{ padding: '.45rem .9rem', background: NAVY, color: GOLD, border: 'none', borderRadius: 7, fontSize: '.75rem', fontWeight: 800, cursor: 'pointer' }}>📋 Copy</button>
                  <button onClick={() => setResult('')} style={{ padding: '.45rem .9rem', background: '#fff', color: '#6b7280', border: '1px solid #e0e0e0', borderRadius: 7, fontSize: '.75rem', cursor: 'pointer' }}>✕ Clear</button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

// ── SETTINGS ───────────────────────────────────
const Settings = ({ user, onLogout }) => (
  <div>
    <Header title="⚙️ Settings" sub="Account and platform configuration" />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
      <Card>
        <div style={{ fontWeight: 800, fontSize: '.95rem', color: NAVY, marginBottom: '1.2rem' }}>👤 Your Profile</div>
        {[['Full Name', user?.full_name], ['Email', user?.email], ['Role', user?.role], ['Department', user?.department || 'N/A']].map(([l, v]) => (
          <div key={l} style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '.72rem', fontWeight: 700, color: '#9ca3af', display: 'block', marginBottom: '.3rem', letterSpacing: .5 }}>{l.toUpperCase()}</label>
            <input defaultValue={v} style={{ width: '100%', padding: '.65rem .9rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '.88rem', boxSizing: 'border-box' }} />
          </div>
        ))}
        <Btn>Save Changes</Btn>
      </Card>
      <Card>
        <div style={{ fontWeight: 800, fontSize: '.95rem', color: NAVY, marginBottom: '1.2rem' }}>🏛️ Organization</div>
        {[['Name', user?.organization?.name], ['Country', 'US'], ['Industry', user?.organization?.industry], ['Employees', user?.organization?.employee_count]].map(([l, v]) => (
          <div key={l} style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '.72rem', fontWeight: 700, color: '#9ca3af', display: 'block', marginBottom: '.3rem', letterSpacing: .5 }}>{l.toUpperCase()}</label>
            <input defaultValue={v} style={{ width: '100%', padding: '.65rem .9rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '.88rem', boxSizing: 'border-box' }} />
          </div>
        ))}
        <Btn>Update Org</Btn>
      </Card>
      <Card style={{ gridColumn: '1/-1', background: `linear-gradient(135deg,${NAVY},${NAVY3})`, border: 'none' }}>
        <div style={{ color: GOLD, fontWeight: 800, fontSize: '.9rem', marginBottom: '.8rem' }}>🔌 System Status</div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          {[['API', 'api.esgova.io'], ['App', 'app.esgova.io'], ['Marketing', 'esgova.com'], ['AI', 'Claude API']].map(([n, u]) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
              <span style={{ color: '#22c55e', fontWeight: 900 }}>●</span>
              <div><div style={{ color: '#fff', fontSize: '.82rem', fontWeight: 700 }}>{n}</div><div style={{ color: 'rgba(255,255,255,.45)', fontSize: '.7rem' }}>{u}</div></div>
            </div>
          ))}
        </div>
        <button onClick={onLogout} style={{ marginTop: '1rem', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.6)', padding: '.5rem 1.2rem', borderRadius: 8, cursor: 'pointer', fontSize: '.82rem', fontWeight: 700 }}>Sign Out</button>
      </Card>
    </div>
  </div>
);

// ── MAIN APP ───────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [nav, setNav] = useState('dashboard');
  const [sideOpen, setSideOpen] = useState(true);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const autoLogin = async () => {
      if (tok()) {
        try { const u = await api('/auth/me'); setUser(u); }
        catch { localStorage.removeItem('esgova_token'); }
      }
      setBooting(false);
    };
    autoLogin();
  }, []);

  const logout = () => { localStorage.removeItem('esgova_token'); setUser(null); setNav('dashboard'); };
  const navTo = (screen) => setNav(screen);

  const NAV = [
    { id: 'dashboard', icon: '🏛️', label: 'Dashboard' },
    { id: 'reports',   icon: '📋', label: 'Reports' },
    { id: 'tasks',     icon: '✅', label: 'Tasks' },
    { id: 'ai',        icon: '🤖', label: 'AI Assistant' },
    { id: 'settings',  icon: '⚙️', label: 'Settings' },
  ];

  if (booting) return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg,${NAVY},${NAVY3})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: `4px solid ${GOLD}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
        <div style={{ color: GOLD, fontWeight: 900, fontSize: '1.1rem', letterSpacing: 2 }}>ESGOVA</div>
        <div style={{ color: 'rgba(255,255,255,.4)', fontSize: '.8rem', marginTop: '.3rem' }}>Loading...</div>
      </div>
    </div>
  );

  if (!user) return <Login onLogin={setUser} />;

  const SCREENS = {
    dashboard: <Dashboard user={user} onNav={navTo} />,
    reports:   <Reports onNav={navTo} />,
    tasks:     <Tasks />,
    ai:        <AIAssistant user={user} />,
    settings:  <Settings user={user} onLogout={logout} />,
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f4fa', fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}`}</style>
      <div style={{ width: sideOpen ? 235 : 62, background: NAVY, minHeight: '100vh', display: 'flex', flexDirection: 'column', transition: 'width .25s', flexShrink: 0 }}>
        <div style={{ padding: sideOpen ? '1.4rem 1.2rem 1rem' : '1.4rem .8rem 1rem', borderBottom: `1px solid ${NAVY3}`, display: 'flex', alignItems: 'center', justifyContent: sideOpen ? 'space-between' : 'center' }}>
          {sideOpen && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <div style={{ width: 26, height: 26, background: `linear-gradient(135deg,${GOLD},${GOLD2})`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: NAVY, fontSize: '.75rem' }}>E</div>
                <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.1rem', letterSpacing: 2 }}>ESGOVA</span>
              </div>
              <div style={{ color: GOLD, fontSize: '.62rem', fontWeight: 700, marginTop: '.2rem', letterSpacing: 1 }}>USIDHR · WASHINGTON DC</div>
            </div>
          )}
          <button onClick={() => setSideOpen(p => !p)} style={{ background: NAVY3, border: 'none', color: GOLD, cursor: 'pointer', fontSize: '.9rem', padding: '.3rem .4rem', borderRadius: 6 }}>{sideOpen ? '◀' : '▶'}</button>
        </div>
        <nav style={{ flex: 1, padding: '.8rem .6rem' }}>
          {NAV.map(item => (
            <button key={item.id} onClick={() => setNav(item.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '.75rem', width: '100%', padding: '.7rem .8rem', borderRadius: 10, border: 'none', cursor: 'pointer', marginBottom: '.2rem', background: nav === item.id ? `linear-gradient(135deg,${NAVY3},${NAVY2})` : 'transparent', color: nav === item.id ? GOLD : 'rgba(255,255,255,.5)', fontWeight: nav === item.id ? 700 : 400, fontSize: '.85rem', textAlign: 'left', borderLeft: nav === item.id ? `3px solid ${GOLD}` : '3px solid transparent' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>{item.icon}</span>
              {sideOpen && item.label}
            </button>
          ))}
        </nav>
        {sideOpen && (
          <div style={{ padding: '1rem', borderTop: `1px solid ${NAVY3}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.7rem', marginBottom: '.8rem' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg,${GOLD},${GOLD2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: NAVY, fontWeight: 800, fontSize: '.75rem', flexShrink: 0 }}>
                {user.full_name?.[0] || 'U'}
              </div>
              <div>
                <div style={{ color: '#fff', fontSize: '.8rem', fontWeight: 700 }}>{user.full_name}</div>
                <div style={{ color: GOLD, fontSize: '.68rem' }}>{user.role}</div>
              </div>
            </div>
            <button onClick={logout} style={{ width: '100%', background: NAVY3, border: 'none', color: 'rgba(255,255,255,.55)', padding: '.5rem', borderRadius: 8, cursor: 'pointer', fontSize: '.78rem', fontWeight: 600 }}>Sign Out</button>
          </div>
        )}
      </div>
      <main style={{ flex: 1, padding: '2rem', overflowY: 'auto', maxHeight: '100vh' }}>
        {SCREENS[nav] || SCREENS.dashboard}
      </main>
    </div>
  );
}
